import React, { useEffect, useState, useMemo, useCallback } from "react";
import SelectOption from "./SelectOption";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { store } from "../../../redux/store";
import * as Yup from "yup";
import { getMerchants } from "../../../services/merchantService";
import { setCurrentBusiness } from "../../../redux/slices/merchantSlice";
import Pagination from "../TablePagination";
import debounce from "lodash.debounce";
import { Modal } from "antd";
import { ReactComponent as CloseIconModal } from "../../../Assets/Icons/popupCloseIcon.svg";
import { useOfferFormContext } from "./OfferFormContext";
import { isLiveOfferStatus } from "./offerApprovalHelpers";

// Validation schema for merchant selection
const merchantSchema = Yup.object().shape({
  merchant: Yup.string().required("Merchant is required"),
});

const MerchantSelection = ({
  stepIndex,
  setStepIndex,
  selectedMerchant,
  setSelectedMerchant,
  setPayloadData,
  editOfferData,
  isEdit,
}) => {
  const dispatch = useDispatch();
  const { registerNextAction, setIsNextDisabled, setNextLabel } = useOfferFormContext();

  // Main view state
  const [merchants, setMerchants] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Modal state (Deprecating but keeping code if needed elsewhere, though user wants in-line now)
  const [viewAllVisible, setViewAllVisible] = useState(false);
  const [modalMerchants, setModalMerchants] = useState([]);
  const [modalSearchQuery, setModalSearchQuery] = useState("");
  const [modalCurrentPage, setModalCurrentPage] = useState(1);
  const [modalTotalPages, setModalTotalPages] = useState(1);
  const [modalCount, setModalCount] = useState(0);
  const [modalLoading, setModalLoading] = useState(false);

  const [offerData, setOfferData] = useState(null);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    setNextLabel("Next");
  }, [setNextLabel]);

  // Register the Next action
  useEffect(() => {
    registerNextAction(() => {
      formik.handleSubmit();
    });
  }, [registerNextAction, payload]);

  // Disable/Enable Next button based on selection
  useEffect(() => {
    setIsNextDisabled(!selectedMerchant && !isEdit);
  }, [selectedMerchant, isEdit, setIsNextDisabled]);

  // Safely update state from Redux store for offer data
  useEffect(() => {
    const updateState = () => {
      try {
        const state = store.getState();
        setOfferData(state?.offerReducer?.createdOffer || null);
      } catch (error) {
        console.warn('MerchantSelection store access error:', error.message);
      }
    };

    const timeoutId = setTimeout(updateState, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  // Fetch merchants function helper
  const fetchMerchantsData = useCallback(async ({ page, search, limit, target = 'main', append = false }) => {
    const isModal = target === 'modal';
    if (isModal) setModalLoading(true);
    else setLoading(true);

    try {
      const params = {
        page,
        limit,
        search: search || null,
      };

      Object.keys(params).forEach((key) => params[key] === null && delete params[key]);

      const result = await dispatch(getMerchants(params))?.unwrap();
      if (result?.success === true) {
        if (isModal) {
          // Slice to 30 items as requested for modal pagination
          setModalMerchants((result.data || []).slice(0, 30));
          setModalTotalPages(result.totalPages || 1);
          setModalCount(result.count || 0);
          setModalCurrentPage(result.currentPage || 1);
        } else {
          const newData = result.data || [];
          // Enforce strict limit on the frontend to prevent showing "all" items if backend over-fetches
          const safeData = newData.slice(0, limit);

          if (append) {
            setMerchants((prev) => {
              // Create a map from previous items for O(1) lookup using String ID
              const merchantMap = new Map(prev.map(m => [String(m._id), m]));

              // Only add new items that don't exist in the map
              safeData.forEach(m => {
                const idStr = String(m._id);
                if (m?._id && !merchantMap.has(idStr)) {
                  merchantMap.set(idStr, m);
                }
              });

              return Array.from(merchantMap.values());
            });
          } else {
            // Deduplicate the fresh batch by ID immediately using String ID
            const uniqueData = Array.from(new Map(safeData.filter(m => m?._id).map((m) => [String(m._id), m])).values());
            setMerchants(uniqueData);
          }
          setCount(result.count || 0);
          setCurrentPage(result.currentPage || 1);
        }
      }
    } catch (error) {
      console.error("Fetch merchants error:", error);
    } finally {
      if (isModal) setModalLoading(false);
      else setLoading(false);
    }
  }, [dispatch]);

  // Debounced search for main view
  const debouncedSearch = useMemo(
    () => debounce((query) => fetchMerchantsData({ page: 1, limit: 16, search: query, target: 'main', append: false }), 500),
    [fetchMerchantsData]
  );

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Debounced search for modal
  const debouncedModalSearch = useMemo(
    () => debounce((query) => fetchMerchantsData({ page: 1, limit: 30, search: query, target: 'modal' }), 500),
    []
  );

  const handleModalSearchChange = (value) => {
    setModalSearchQuery(value);
    debouncedModalSearch(value);
  };

  const handleModalPageChange = (newPage) => {
    fetchMerchantsData({ page: newPage, limit: 30, search: modalSearchQuery, target: 'modal' });
  };

  // Ensure rendering never shows duplicates even if state gets dirty
  const uniqueMerchants = useMemo(() => {
    const seenIds = new Set();
    const seenNames = new Set();

    return merchants.filter(m => {
      // 1. Filter out invalid items
      const idStr = m?._id ? String(m._id) : null;
      if (!idStr) return false;

      // 2. Strict ID Deduplication
      if (seenIds.has(idStr)) return false;
      seenIds.add(idStr);

      // 3. Name Deduplication (Safety net for backend data issues)
      // If user says there's only 1 "Etto", we shouldn't show 2 even if IDs differ.
      const nameKey = m?.businessName?.trim().toLowerCase();
      if (nameKey) {
        if (seenNames.has(nameKey)) return false;
        seenNames.add(nameKey);
      }

      return true;
    });
  }, [merchants]);

  const handleViewMore = () => {
    if (loading) return;
    const nextPage = currentPage + 1;
    fetchMerchantsData({ page: nextPage, limit: 16, search: searchQuery, target: 'main', append: true });
  };

  // Initial load for main view
  useEffect(() => {
    fetchMerchantsData({ page: 1, limit: 16, search: null, target: 'main' });
  }, []);

  // Sync with modal visibility
  useEffect(() => {
    if (viewAllVisible) {
      setModalSearchQuery(""); // Clear modal search when opening
      fetchMerchantsData({ page: 1, limit: 30, search: "", target: 'modal' });
    }
  }, [viewAllVisible]);

  const formik = useFormik({
    initialValues: {
      merchant: editOfferData?.merchant?.name || "",
    },
    validationSchema: merchantSchema,
    onSubmit: async (values) => {
      // Logic for moving to next step
      if (payload || (isEdit && selectedMerchant)) {
        setStepIndex(stepIndex + 1);
      } else {
        formik.setFieldTouched("merchant", true);
      }
    },
  });

  useEffect(() => {
    if (editOfferData?.merchant) {
      setSelectedMerchant(editOfferData.merchant);
      formik.setFieldValue("merchant", editOfferData.merchant.businessName);
    }
  }, [editOfferData]);

  // (Removed aggressive re-selection effect that caused bugs)

  useEffect(() => {
    setPayloadData(payload);
  }, [payload]);

  const handleClick = async (id, sourceList) => {
    if (!id) return;

    // If clicking the already selected merchant, unselect it
    if (selectedMerchant?._id === id) {
      setSelectedMerchant(null);
      dispatch(setCurrentBusiness(null));
      formik.setFieldValue("merchant", "");
      setPayload(null);
      return;
    }

    const combinedList = [...merchants, ...(modalMerchants || []), ...(sourceList || [])];
    const merchant = combinedList.find((merch) => merch?._id === id) || null;
    if (!merchant) return;

    // Set local and global selection
    setSelectedMerchant(merchant);
    dispatch(setCurrentBusiness(merchant));
    formik.setFieldValue("merchant", merchant?.businessName || "");

    // Build payload for next steps
    const newPayload = { 
      merchantId: merchant?._id, 
      isCreated: isLiveOfferStatus(editOfferData?.status || offerData?.status)
    };
    const currentOfferId = editOfferData?._id || offerData?._id;
    if (currentOfferId) {
      newPayload.id = currentOfferId;
      newPayload.offerId = currentOfferId;
    }
    setPayload(newPayload);
  };

  const { merchant } = formik.values;
  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
  } = formik;

  const isMerchantClicked = (merch) => {
    return selectedMerchant?._id === merch?._id;
  };

  const isBusy = loading;
  const hasMore = count > 16;

  return (
    <div className="w-full flex flex-col items-center justify-start flex-grow">
      <div className="w-full max-w-[800px] mt-[30px] md:mt-[60px] relative px-4">

        {/* Title and Subtitle */}
        <div className="mb-6 md:mb-8 w-full text-left">
          <h2 className="text-[22px] md:text-[32px] font-[600] text-[#303030] font-poppins text-left leading-tight">
            You can Create Offer on behalf of <span className="text-[#00ADEF]">Merchant</span>
          </h2>
          <p className="font-poppins font-[400] text-[14px] md:text-[18px] text-[#767676] mt-2">
            Choose the merchant for whom you are creating offer
          </p>
        </div>

        {/* Search Bar */}
        <div className="w-full max-w-[380px] mb-8">
          <input
            type="text"
            placeholder="Search merchants..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full h-[45px] px-4 border border-[#D9D9D9] rounded-[8px] focus:outline-none focus:ring-1 focus:ring-[#00ADEF] focus:border-[#00ADEF] font-poppins text-[14px] md:text-[16px]"
          />
        </div>

        {/* Merchants List - Grid Layout */}
        <div className="mt-2">
          {isBusy && uniqueMerchants.length === 0 ? (
            <div className="flex justify-center items-center py-20">
              <span className="offer-inline-spinner" />
            </div>
          ) : uniqueMerchants?.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full">
                {uniqueMerchants.map((merch) => (
                  <SelectOption
                    key={merch._id}
                    icon={null}
                    clicked={isMerchantClicked(merch)}
                    title={merch?.businessName}
                    handleClick={() => handleClick(merch?._id, uniqueMerchants)}
                    showCheckmark={true}
                    noMargin={true}
                  />
                ))}
              </div>
              {!searchQuery && uniqueMerchants.length < count && (
                <div className="mt-4 text-left pb-6">
                  <span className="view-all-link" onClick={handleViewMore}>
                    {loading ? "Loading..." : "View More"}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20 border border-dashed border-[#D9D9D9] rounded-[10px]">
              <p className="text-[#767676] font-poppins text-[16px]">
                {searchQuery ? "No merchants found matching your search" : "No merchants available"}
              </p>
            </div>
          )}
        </div>

        {/* Validation Error */}
        {formik.errors?.merchant && formik.touched?.merchant ? (
          <div className="mt-4 pb-10">
            <span className="text-red-600 text-[12px] font-[600] font-poppins">
              Merchant is required.
            </span>
          </div>
        ) : null}
      </div>

    </div>
  );
};

export default MerchantSelection;
